import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import chevronRightSmall from "@ingka/ssr-icon/paths/chevron-right-small";
import chevronRight from "@ingka/ssr-icon/paths/chevron-right";
import arrowRight from "@ingka/ssr-icon/paths/arrow-right";
import linkOut from "@ingka/ssr-icon/paths/link-out";
import copy from "@ingka/ssr-icon/paths/copy";
import Checkbox from "@ingka/checkbox";
import RadioButton from "@ingka/radio-button";
import Switch from "@ingka/switch";
import SSRIcon from "@ingka/ssr-icon";
import Image from "@ingka/image";
import AspectRatioBox from "@ingka/aspect-ratio-box";
import PaymentLogo from "@ingka/payment-logo";
import React, { forwardRef } from "react";
import cx from "classnames";
import Avatar from "@ingka/avatar";
const ListViewItem = forwardRef((props, ref) => {
  const {
    avatarProps,
    prefix = "",
    id,
    control = "default",
    name,
    onClick,
    onChange,
    title,
    description,
    addOn,
    leadingIcon,
    image,
    imageAltText = "",
    paymentLogo,
    size = "medium",
    inset,
    emphasised,
    disabled,
    className,
    controlProps,
    imageProps,
    quantityLabel,
    href,
    newWindow,
    controlIcon = "linkOut",
    leadingIconPosition = "center",
    showDivider = true,
    ...otherProps
  } = props;
  const baseClass = `${prefix}list-view-item`;
  const controlId = id + "__control";
  const controlTypes = {
    DEFAULT: "default",
    CHECKBOX: "checkbox",
    RADIOBUTTON: "radiobutton",
    SWITCH: "switch",
    NAVIGATIONAL: "navigational",
    LINK: "link"
  };
  const classes = cx(
    baseClass,
    disabled && control !== controlTypes.DEFAULT && `${baseClass}--disabled`,
    !showDivider && `${baseClass}__no-divider`,
    className
  );
  const itemClasses = cx(
    control === controlTypes.DEFAULT ? `${baseClass}__default` : `${baseClass}__action`,
    inset && `${baseClass}__inset`,
    image && `${baseClass}__with-image`
  );
  const trailingIcon = {
    chevronRight,
    chevronRightSmall,
    arrowRight,
    linkOut,
    copy
  };
  const TrailingIcon = (control === "navigational" || control === "link") && controlIcon ? trailingIcon[controlIcon] && /* @__PURE__ */ jsx("span", { className: `${baseClass}__control-icon`, children: /* @__PURE__ */ jsx(SSRIcon, { prefix, paths: trailingIcon[controlIcon] }) }) : null;
  const formComponentProps = {
    subtle: true,
    id: controlId,
    name,
    disabled,
    onChange,
    prefix,
    ...controlProps,
    value: controlProps?.value || ""
  };
  let FormControl;
  if (control === "checkbox") {
    FormControl = /* @__PURE__ */ jsx(Checkbox, { ...formComponentProps, ref: React.createRef() });
  } else if (control === "radiobutton") {
    FormControl = /* @__PURE__ */ jsx(RadioButton, { ...formComponentProps, ref: React.createRef() });
  } else if (control === "switch") {
    FormControl = /* @__PURE__ */ jsx(Switch, { ...formComponentProps });
  }
  let avatarSize = "small";
  if (size === "large") {
    avatarSize = "medium";
  } else if (size === "small") {
    avatarSize = "xsmall";
  }
  const leadingOptsMap = {
    ["image"]: /* @__PURE__ */ jsx("span", { className: `${baseClass}__image`, children: /* @__PURE__ */ jsx(AspectRatioBox, { prefix, ratio: "square", children: /* @__PURE__ */ jsx(Image, { src: image, prefix, alt: imageAltText, ...imageProps }) }) }),
    ["icon"]: /* @__PURE__ */ jsx(SSRIcon, { prefix, className: `${baseClass}__icon-${leadingIconPosition}`, paths: leadingIcon }),
    ["paymentLogo"]: /* @__PURE__ */ jsx(
      PaymentLogo,
      {
        prefix,
        className: `${baseClass}__payment-logo`,
        size: size === "small" ? "small" : "medium",
        ...paymentLogo
      }
    ),
    ["avatar"]: avatarProps?.screenReaderText ? /* @__PURE__ */ jsx(Avatar, { className: `${baseClass}__avatar`, prefix, ...avatarProps, size: avatarSize, onClick: void 0 }) : null
  };
  let leadingMedia = null;
  if (image) {
    leadingMedia = leadingOptsMap.image;
  } else if (paymentLogo && !image) {
    leadingMedia = leadingOptsMap.paymentLogo;
  } else if (leadingIcon && !image && !paymentLogo) {
    leadingMedia = leadingOptsMap.icon;
  } else if (avatarProps) {
    leadingMedia = leadingOptsMap.avatar;
  }
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("span", { className: `${baseClass}__wrapper`, children: [
      leadingMedia,
      /* @__PURE__ */ jsxs("span", { className: `${baseClass}__label-container`, children: [
        /* @__PURE__ */ jsx("span", { className: cx(`${baseClass}__title`, emphasised && `${baseClass}__title--emphasised`), children: title }),
        description && size !== "small" && /* @__PURE__ */ jsx("span", { className: `${baseClass}__description`, children: description }),
        addOn && size !== "small" && /* @__PURE__ */ jsx("span", { className: `${baseClass}__addon`, children: addOn })
      ] })
    ] }),
    quantityLabel && /* @__PURE__ */ jsx("span", { className: `${baseClass}__quantity-label`, children: quantityLabel }),
    TrailingIcon ? TrailingIcon : FormControl
  ] });
  return /* @__PURE__ */ jsx(
    "li",
    {
      id,
      ref,
      className: classes,
      /* eslint-disable no-nested-ternary */
      children: control === controlTypes.NAVIGATIONAL ? /* @__PURE__ */ jsx(
        "button",
        {
          className: itemClasses,
          onClick: !disabled ? onClick : void 0,
          ...otherProps,
          disabled,
          children: content
        }
      ) : control === controlTypes.LINK ? /* @__PURE__ */ jsx(
        "a",
        {
          className: itemClasses,
          href: href && !disabled ? href : void 0,
          target: href && newWindow && !disabled ? newWindow : void 0,
          ...otherProps,
          children: content
        }
      ) : control === controlTypes.DEFAULT ? /* @__PURE__ */ jsx("span", { className: itemClasses, ...otherProps, children: content }) : /* @__PURE__ */ jsx("label", { className: itemClasses, ...otherProps, children: content })
    }
  );
});
ListViewItem.displayName = "ListViewItem";
var ListViewItem_default = ListViewItem;
export {
  ListViewItem_default as default
};
